import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { DataTable } from '../../components/Tables';
import PageHeader from '../shared/PageHeader';
import ConfirmDeleteModal from '../../components/common/ConfirmDeleteModal';
import { formsAndPrecedentsApi, extractList } from '../../api/facades';

const FormsListPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const { data: res, isLoading } = useQuery({
    queryKey: ['forms-and-precedents', search],
    queryFn: () => formsAndPrecedentsApi.list({ search: search || undefined }),
  });
  const rows = extractList(res);

  const deleteMu = useMutation({
    mutationFn: (id: number) => formsAndPrecedentsApi.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['forms-and-precedents'] });
      setDeleteId(null);
      toast.success('Form or precedent deleted.');
    },
    onError: (e: any) => toast.error(e?.message || 'Delete failed.'),
  });

  const columns = [
    { key: 'name', label: 'Name' },
    { key: 'document_name', label: 'Document' },
    { key: 'keywords', label: 'Keywords', render: (r: any) => (r.keywords ? String(r.keywords).slice(0, 40) + (String(r.keywords).length > 40 ? '…' : '') : '—') },
  ];

  return (
    <div>
      <PageHeader hideTitle subtitle="Manage forms and precedents." primaryAction={{ label: 'New', to: '/dashboard/forms-and-precedents/new' }} />
      <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6">
        <div className="mb-4">
          <input type="search" placeholder="Search" value={search} onChange={(e) => setSearch(e.target.value)} className="rounded-lg border border-gray-300 bg-white dark:bg-slate-800 dark:border-gray-600 py-2 px-3 text-sm w-48" />
      </div>
        <DataTable columns={columns} data={rows} loading={isLoading} emptyMessage="No forms or precedents." onRowClick={(id) => navigate(`/dashboard/forms-and-precedents/${id}`)} onEdit={(id) => navigate(`/dashboard/forms-and-precedents/${id}/edit`)} onDelete={(id) => setDeleteId(Number(id))} />
      </div>
      <ConfirmDeleteModal open={deleteId != null} onClose={() => setDeleteId(null)} onConfirm={() => deleteId != null && deleteMu.mutate(deleteId)} title="Delete form or precedent" message="Are you sure? This cannot be undone." loading={deleteMu.isPending} />
    </div>
  );
};

export default FormsListPage;
